import { buildFixtureSlug } from "@/lib/entities";
import { LockedHint } from "@/components/predictions/LockedHint";
import { uiText } from "@/lib/i18n/uiText";
import type { PublicPick } from "@/lib/publicPicks.functions";
import { GATES_SUSPENDED } from "@/lib/accessMode";

// The first two upcoming picks are free featured picks (also unmasked server-side).
const VISIBLE_FREE = 2;

function previewHref(p: PublicPick) {
  return `/fixtures/${buildFixtureSlug(p.team_home, p.team_away, p.kickoff_time)}`;
}

/** Plain, server-rendered list of upcoming picks so the content exists before scripts run. */
export function ServerPicksList({ picks }: { picks: PublicPick[] }) {
  if (!picks.length) return null;
  return (
    <section data-tap-zone aria-labelledby="upcoming-picks-heading" className="mt-10 rounded-xl border border-border bg-card/40 p-5">
      <h2 id="upcoming-picks-heading" className="text-xl font-semibold mb-1">{uiText("Upcoming picks at a glance")}</h2>
      <p className="text-sm text-muted-foreground mb-4">
         {uiText("Every pick comes from the Statlign engine. It weighs team form, last-10 goals, head-to-head history, formations and team news, then shows its confidence and the main reason. Picks are probabilities, never guarantees.")} </p>
      <ol className="divide-y divide-border">
        {picks.map((p, i) => {
          const open = GATES_SUSPENDED || i < VISIBLE_FREE || !!p.predicted_outcome;
          const kickoff = new Date(p.kickoff_time).toISOString().replace("T", " ").slice(0, 16) + " UTC";
          return (
            <li key={p.id} className="py-3">
              <article>
                <h3 className="font-medium">
                  <a href={previewHref(p)} className="hover:underline">{p.team_home}  {uiText("vs")} {p.team_away}</a>
                </h3>
                <p className="text-xs text-muted-foreground">{p.league ?? "Football"} · {kickoff}</p>
                <p className="text-sm mt-1">
                  {(open || p.predicted_outcome) && p.predicted_outcome ? (
                    <>
                      {uiText(p.market_type)}: <strong>{uiText(p.predicted_outcome)}</strong>
                      {p.odds ? uiText(" · odds {{v0}}", { v0: p.odds.toFixed(2) }) : ""}
                      {p.confidence_score != null ? uiText(" · {{v0}}% confidence", { v0: Math.round(p.confidence_score) }) : ""}
                    </>
                  ) : (
                    <span className="text-muted-foreground"><LockedHint variant="market" /></span>
                  )}
                </p>
                {p.predicted_outcome && p.reason && <p className="text-xs text-muted-foreground mt-1">{p.reason}</p>}
              </article>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function picksJsonLd(picks: PublicPick[]) {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Upcoming football predictions",
    itemListElement: picks.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "SportsEvent",
        name: `${p.team_home} vs ${p.team_away}`,
        startDate: p.kickoff_time,
        sport: "Soccer",
        url: `https://statlign.com${previewHref(p)}`,
        homeTeam: { "@type": "SportsTeam", name: p.team_home },
        awayTeam: { "@type": "SportsTeam", name: p.team_away },
        eventStatus: "https://schema.org/EventScheduled",
        location: { "@type": "Place", name: p.league ?? "TBC" },
      },
    })),
  });
}
